<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-alert />
        <p>
          Workforce means providers, employees, volunteers, trainees, independent contractors, and other persons whose conduct, in the
          performance of work for your organization (covered entity or business associate), is under the direct control of your organization,
          whether or not they are paid by your organization.
        </p>
        <hm-spinner v-if="!ready" />
      </b-col>
    </b-row>

    <b-row v-show="ready">
      <b-col>
        <b-button type="button" variant="primary" class="px-4" to="/workforce-edit">Add Workforce Member</b-button>
      </b-col>
    </b-row>

    <b-row class="pt-4" v-show="ready">
      <b-col>
        <h4>Workforce Members</h4>
      </b-col>
    </b-row>

    <b-row v-show="ready">
      <b-col>
        <b-table
          class="p-1"
          responsive
          bordered
          hover
          show-empty
          empty-html="No Active Workforce Members<br/>Solo practice? Add yourself as a workforce member."
          :items="activeWorkforce"
          :fields="columns"
          :sort-compare="sortCompare"
          @row-clicked="rowClick"
        >
          <template slot="cell(access)" slot-scope="data">
            <hm-roleslist :role="role" :item="data.item.role" />
          </template>
          <template slot="cell(training)" slot-scope="data">
            <hm-traininglist :training="data.value" />
          </template>
        </b-table>
      </b-col>
    </b-row>

    <b-row class="pt-4" v-show="ready">
      <b-col>
        <h4>Removed Workforce Members</h4>
      </b-col>
    </b-row>

    <b-row v-show="ready">
      <b-col>
        <b-table
          class="p-1"
          responsive
          bordered
          hover
          show-empty
          empty-text="No Removed Workforce Members"
          :items="inactiveWorkforce"
          :fields="columnsInactive"
          :sort-compare="sortCompare"
          @row-clicked="rowClick"
        >
          <template slot="cell(separation)" slot-scope="data">
            <span v-if="data.value.amicable" class="text-success">Amicable</span>
            <span v-else class="text-danger">Hostile</span>
          </template>
          <template slot="cell(tasks)" slot-scope="data">
            <span v-if="data.item.separation.inventory && data.item.separation.ePHI && data.item.separation.credentials">
              <i class="icon-check text-success"></i> Complete
            </span>
            <span v-else>
              <i class="icon-exclamation text-danger"></i> Pending
            </span>
          </template>
        </b-table>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { trainingService, workforceService } from '@/services'
import Enumerable from 'linq/linq.js'

export default {
  name: 'WorkforceList',
  data() {
    return {
      workforce: [],
      role: {
        writtenDesc: false,
        segregation: false,
        globalAccess: true,
        roles: {
        }
      },
      training: {},
      columns: [{
        key: 'name',
        label: 'Name',
        sortable: true
      }, {
        key: 'role',
        label: 'Role',
        sortable: true,
        formatter: (value, key, item) => {
          return this.roleMap.Roles[value];
        }
      }, {
        key: 'access',
        label: 'ePHI Access'
      }, {
        key: 'training',
        label: 'Training'
      }],
      columnsInactive: [{
        key: 'name',
        label: 'Name',
        sortable: true
      }, {
        key: 'role',
        label: 'Role',
        sortable: true,
        formatter: (value, key, item) => {
          return this.roleMap.Roles[value];
        }
      }, {
        key: 'separation',
        label: 'Separation'
      }, {
        key: 'tasks',
        label: 'Tasks'
      }],
      ready: false
    }
  },
  computed: {
    ...mapState('maps', ['roleMap', 'trainingMap']),
    ...mapState('maps', {
      roleMapRoleArray() {
        return Enumerable.from(this.roleMap.Roles).toArray();
      },
      activeWorkforce() {
        return Enumerable.from(this.workforce).where(x => { return x.active }).toArray();
      },
      inactiveWorkforce() {
        return Enumerable.from(this.workforce).where(x => { return !x.active }).toArray();
      }
    })
  },
  methods: {
    ...mapActions({
      alertException: 'alert/exception'
    }),
    sortCompare(a, b, key) {
      if (key === 'role') { // custom sorting for the 'role' column
        return this.roleMap.Roles[a[key]].localeCompare(this.roleMap.Roles[b[key]]);
      }
      if (typeof a[key] === 'number' && typeof b[key] === 'number') {
        return a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0
      }
      return String(a[key]).localeCompare(String(b[key]));
    },
    rowClick(record, index) {
      this.$router.push({ name: 'WorkforceEdit', params: { id: record.id } });
    }
  },
  created() {
    this.ready = false;

    Promise.all([
      this.$store.dispatch('maps/fetchRoleMap'),
      this.$store.dispatch('maps/fetchTrainingMap'),
      this.$store.dispatch('maps/fetchWorkforceMap'),
      workforceService.getRole(),
      trainingService.getTrainingCache(),
      workforceService.getWorkforce()
    ]).then((values) => {
      this.role = values[3];
      this.training = values[4];
      let workforce = values[5];

      // join workforce list to training
      let emptyTraining = Enumerable.from(this.trainingMap.Training)
        .toObject(function (k) { return k.key; }, function (v) { return ''; });

      this.workforce = Enumerable.from(workforce)
        .groupJoin(this.training.training,
          function (o) { return o.id; },
          function (i) { return i.id; },
          function (o, i) {
            o.training = i.firstOrDefault(null, { training: emptyTraining }).training;
            return o;
          })
        .toArray();
    }).catch((err) => {
      this.alertException({ err, message: 'Error loading data.' });
    }).finally(() => {
      this.ready = true;
    })
  }
}
</script>